// Client for the admin assistant service (admin-agent). Deployed builds go through the gateway
// (/api/admin/agent/*) with the user's session headers. For local development set
// VITE_ADMIN_AGENT_URL (e.g. http://localhost:7420) to call the service directly.
import { ADMIN_AGENT_URL, GATEWAY_API_URL } from '@admin-panel/config/env';
import { buildGatewayHeaders } from '@admin-panel/providers';

const DIRECT = ADMIN_AGENT_URL;
const BASE = (DIRECT || `${GATEWAY_API_URL}/agent`).replace(/\/$/, '');

export type ChatEvent =
  | { t: 'text'; d: string }
  | { t: 'msg_end' }
  | { t: 'tool'; id: string; label: string; sql?: string }
  | { t: 'tool_end'; id: string; ok: boolean; preview: string }
  | { t: 'error'; d: string }
  /** The model call failed and is being retried; d is the reply text to keep. */
  | { t: 'retry'; d: string; label: string }
  | { t: 'done' };

/** A table result the assistant saved (generic block `table`). */
export interface ResultTable {
  columns: Array<string>;
  rows: Array<Array<string | number | null>>;
  total: number;
}

function headers(extra?: Record<string, string>): Headers {
  const h = buildGatewayHeaders(extra);
  if (DIRECT) h.set('x-agent-dev-user', 'local');
  return h;
}

async function call<T>(path: string, body?: unknown): Promise<T> {
  const res = await fetch(`${BASE}${path}`, { method: body ? 'POST' : 'GET', headers: headers(), body: body ? JSON.stringify(body) : undefined });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error((data as { error?: string }).error || `Request failed (${res.status})`);
  return data as T;
}

export type AgentClient = ReturnType<typeof createAgentClient>;

/** A client bound to one assistant scenario (e.g. 'stock-transfer'). */
export function createAgentClient(scenario: string) {
  const q = (draftId: string) => `scenario=${encodeURIComponent(scenario)}&draftId=${encodeURIComponent(draftId)}`;
  return {
    scenario,
    status: () => call<{ model: string; scenarios: Array<{ id: string; title: string }> }>('/status'),
    /** A result the assistant saved in this draft's session, by ref (r1, p2, t3...). */
    result: <T>(draftId: string, ref: string) => call<T>(`/result/${encodeURIComponent(ref)}?${q(draftId)}`),
    reset: (draftId: string) => call<{ ok: boolean }>('/reset', { scenario, draftId }),
    /** A scenario endpoint, e.g. route('grid', draftId, {styles}). */
    route: <T>(name: string, draftId: string, body: Record<string, unknown>) => call<T>(`/s/${scenario}/${name}`, { ...body, scenario, draftId }),

    /** Streams one assistant turn. Resolves when the turn ends. */
    async chat(draftId: string, message: string, ui: unknown, onEvent: (e: ChatEvent) => void, signal?: AbortSignal) {
      const res = await fetch(`${BASE}/chat`, {
        method: 'POST',
        headers: headers({ Accept: 'text/event-stream' }),
        body: JSON.stringify({ scenario, draftId, message, ui }),
        signal,
      });
      if (!res.ok || !res.body) {
        const e = await res.json().catch(() => ({}));
        throw new Error((e as { error?: string }).error || `Assistant unavailable (${res.status})`);
      }
      const reader = res.body.getReader();
      const dec = new TextDecoder();
      let buf = '';
      for (;;) {
        const { value, done } = await reader.read();
        if (done) break;
        buf += dec.decode(value, { stream: true });
        let i: number;
        while ((i = buf.indexOf('\n\n')) >= 0) {
          const raw = buf.slice(0, i);
          buf = buf.slice(i + 2);
          if (raw.startsWith('data: ')) onEvent(JSON.parse(raw.slice(6)) as ChatEvent);
        }
      }
    },
  };
}

/** Machine blocks the assistant appends to a reply as ```kind {json}``` fences. `options` and
 *  `table` are generic; scenarios define their own kinds (e.g. scope, moves). */
export type ReplyBlocks = { options?: Array<string>; table?: { ref: string; title?: string } } & Record<string, unknown>;

/** `blocks` holds the last block of each kind; `all` holds every block in reply order, since a reply
 *  can carry several of one kind (e.g. one style list and one plan per batch of loaded styles). */
export function splitReply(text: string): { clean: string; blocks: ReplyBlocks; all: Array<{ kind: string; value: unknown }> } {
  const blocks: Record<string, unknown> = {};
  const all: Array<{ kind: string; value: unknown }> = [];
  const clean = text.replace(/```([a-z][\w-]*)\s*\n([\s\S]*?)(```|$)/g, (m, kind: string, body: string, end: string) => {
    if (end) {
      try { blocks[kind] = JSON.parse(body); } catch { return m; /* not a block: keep it as text */ }
      all.push({ kind, value: blocks[kind] });
    }
    return '';
  });
  return { clean: clean.trim(), blocks: blocks as ReplyBlocks, all };
}

/** Every block of one kind in a reply, in order. */
export const blocksOf = <T>(text: string, kind: string): Array<T> =>
  splitReply(text).all.filter(b => b.kind === kind).map(b => b.value as T);
